import liff from '@line/liff'
import {
  LiffMockPlugin,
  type ExtendedInit,
  type LiffMockApi,
} from '@line/liff-mock'

/**
 * LINE user profile information
 */
export type LineProfile = {
  displayName?: string
  pictureUrl?: string
  statusMessage?: string
  userId?: string
}

/**
 * LIFF initialization result containing user credentials and profile
 */
interface LiffInfo {
  /** LINE ID token for authentication */
  lineIdToken: string
  /** LINE user profile data */
  lineProfile: LineProfile
}

/**
 * Extended LIFF interface with mock API support
 */
interface LiffWithMock {
  $mock: LiffMockApi
}

/**
 * Whether LIFF mock mode is enabled via environment variable
 */
export const isMockEnabled = import.meta.env.VITE_LIFF_MOCK === 'true'

const DEFAULT_MOCK_PICTURE_URL = 'https://i.pravatar.cc/150?img=8'
const DEFAULT_MOCK_ID_TOKEN = 'mock-line-token'

/**
 * Install LIFF Mock plugin when mock mode is enabled
 */
if (isMockEnabled) {
  liff.use(new LiffMockPlugin())
}

/**
 * Setup default mock data with initial "not logged in" state.
 * Called during LIFF initialization to prepare mock environment.
 * @internal
 */
function setupDefaultMockData(): void {
  if (!isMockEnabled) return
  const liffWithMock = liff as unknown as LiffWithMock
  liffWithMock.$mock.set({
    isLoggedIn: false,
    login: undefined, // Mock login to do nothing (prevent redirect)
    getAccessToken: 'mock-access-token',
    getIDToken: DEFAULT_MOCK_ID_TOKEN,
    getProfile: {
      displayName: 'Mock Account',
      userId: 'U1234567890abcdef',
      pictureUrl: DEFAULT_MOCK_PICTURE_URL,
      statusMessage: 'Mock user for testing',
    },
  })
}

/**
 * Set mock login state to true (simulates successful LINE login).
 * @internal
 */
function setMockLoggedIn(): void {
  if (!isMockEnabled) return
  const liffWithMock = liff as unknown as LiffWithMock
  liffWithMock.$mock.set((prev) => ({
    ...prev,
    isLoggedIn: true,
  }))
}

/**
 * Options for customizing mock profile data
 */
interface MockProfileOptions extends Partial<LineProfile> {
  /** Custom ID token for mock authentication */
  idToken?: string
}

/**
 * Logout current user and redirect to LINE login page.
 * Preserves the current URL (including query parameters) as the redirect URI.
 */
export function liffLogoutAndRelogin(): void {
  liff.logout()
  const currentUrl = new URL(window.location.href)
  const queryString = currentUrl.search
  const redirectUri = `${currentUrl.origin}${currentUrl.pathname}${queryString}`
  liff.login({ redirectUri })
}

/**
 * Get the current LINE user's profile information.
 * @returns Promise resolving to the user's LINE profile
 */
export async function getLineProfile(): Promise<LineProfile> {
  const profile = await liff.getProfile()
  return {
    displayName: profile.displayName,
    pictureUrl: profile.pictureUrl,
    userId: profile.userId,
  }
}

/**
 * Initialize LIFF SDK and authenticate user.
 *
 * In mock mode (VITE_LIFF_MOCK=true):
 * - Uses mock plugin to simulate LINE login
 * - Automatically sets logged in state
 *
 * In production mode:
 * - Redirects to LINE login if not authenticated
 * - Returns undefined during redirect
 *
 * @param liffId - The LIFF application ID from LINE Developers Console
 * @returns Promise resolving to user info if logged in, undefined if redirecting to login
 */
export async function initLiff(liffId: string): Promise<LiffInfo | undefined> {
  const init = liff.init as ExtendedInit
  await init({ liffId, mock: isMockEnabled })

  setupDefaultMockData()

  if (!liff.isLoggedIn()) {
    if (isMockEnabled) {
      liff.login()
      setMockLoggedIn()
    } else {
      liffLogoutAndRelogin()
      return
    }
  }

  const lineProfile = await getLineProfile()
  const lineIdToken = liff.getIDToken() || ''

  return { lineIdToken, lineProfile }
}
